import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiMail, FiSend, FiCheckCircle, FiAlertCircle, FiUser, FiMessageSquare, FiPhone } from 'react-icons/fi';
import { sendContactEmail } from '../../services/api';

const ContactSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(0deg, #0a192f 0%, #112240 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, rgba(100, 255, 218, 0), rgba(100, 255, 218, 0.3), rgba(100, 255, 218, 0));
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled(motion.h2)`
  font-size: 3rem;
  color: #ccd6f6;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;

  span {
    color: #64ffda;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: #64ffda;
    border-radius: 2px;
  }
`;

const Subtitle = styled(motion.p)`
  color: #8892b0;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
`;

const FormContainer = styled(motion.div)`
  background: rgba(17, 34, 64, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(100, 255, 218, 0.1);
`;

const FormGroup = styled(motion.div)`
  margin-bottom: 25px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #64ffda;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #8892b0;
    font-size: 1.2rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px 12px 45px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
  color: #e6f1ff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #64ffda;
    box-shadow: 0 0 20px rgba(100, 255, 218, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: #8892b0;
    opacity: 0.7;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px 12px 45px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
  color: #e6f1ff;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #64ffda;
    box-shadow: 0 0 20px rgba(100, 255, 218, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: #8892b0;
    opacity: 0.7;
  }
`;

const RequiredInfo = styled.span`
  color: #8892b0;
  font-size: 0.8rem;
  margin-left: 8px;
  opacity: 0.8;
`;

const SubmitButton = styled(motion.button)`
  background: transparent;
  border: 2px solid #64ffda;
  color: #64ffda;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  width: 100%;
  font-weight: 600;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: rgba(100, 255, 218, 0.1);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.5s ease;
    border-radius: 8px;
  }

  &:hover {
    transform: translateY(-2px);
    
    &:before {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const Notification = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 25px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ type }) => (type === 'success' ? '#0a192f' : '#fff')};
  background: ${({ type }) => (type === 'success' ? '#64ffda' : '#ff6b6b')};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  z-index: 1000;
`;

const LoadingSpinner = styled(motion.div)`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(100, 255, 218, 0.3);
  border-top: 3px solid #64ffda;
  border-radius: 50%;
`;

const SuccessOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 25, 47, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(17, 34, 64, 0.95);
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  border: 2px solid #64ffda;
  max-width: 90%;
  width: 400px;

  h3 {
    color: #64ffda;
    font-size: 1.5rem;
    margin-bottom: 20px;
  }

  p {
    color: #8892b0;
    margin-bottom: 30px;
  }

  button {
    background: #64ffda;
    color: #0a192f;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(100, 255, 218, 0.2);
    }
  }
`;

const ContactInfoContainer = styled(motion.div)`
  margin-bottom: 40px;
  text-align: center;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 10px 0;
  color: #8892b0;
  
  svg {
    color: #64ffda;
  }
  
  a {
    color: #64ffda;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #8892b0;
    }
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [notification, setNotification] = useState(null);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendContactEmail(formData);
      setShowSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      showNotification(error.message || 'Une erreur est survenue', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Contactez <span>Moi</span>
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            N'hésitez pas à me contacter pour discuter de vos projets
          </Subtitle>
        </Header>

        <ContactInfoContainer
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <ContactItem>
            <FiMail />
            <a href="mailto:isaac.naranjo.e.g@gmail.com">isaac.naranjo.e.g@gmail.com</a>
          </ContactItem>
          <ContactItem>
            <FiPhone />
            <a href="tel:+41762235612">+41(0)76.223.56.12</a>
          </ContactItem>
        </ContactInfoContainer>

        <FormContainer
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <form onSubmit={handleSubmit}>
            <FormGroup variants={itemVariants}>
              <Label>
                <FiUser /> Nom
                <RequiredInfo>(2 caractères minimum)</RequiredInfo>
              </Label>
              <InputWrapper>
                <FiUser />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Votre nom"
                  minLength="2"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup variants={itemVariants}>
              <Label>
                <FiMail /> Email
              </Label>
              <InputWrapper>
                <FiMail />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="votre@email.com"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup variants={itemVariants}>
              <Label>
                <FiMessageSquare /> Message
                <RequiredInfo>(3 caractères minimum)</RequiredInfo>
              </Label>
              <InputWrapper>
                <FiMessageSquare />
                <TextArea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Votre message..."
                  minLength="3"
                />
              </InputWrapper>
            </FormGroup>

            <SubmitButton
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <LoadingSpinner
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                <>
                  <FiSend /> Envoyer le message
                </>
              )}
            </SubmitButton>
          </form>
        </FormContainer>

        <AnimatePresence>
          {notification && (
            <Notification
              type={notification.type}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {notification.type === 'success' ? (
                <FiCheckCircle />
              ) : (
                <FiAlertCircle />
              )}
              {notification.message}
            </Notification>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showSuccess && (
            <SuccessOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SuccessMessage
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <FiCheckCircle style={{ fontSize: '3rem', color: '#64ffda', marginBottom: '20px' }} />
                <h3>Message envoyé !</h3>
                <p>Merci pour votre message. Je vous répondrai dans les plus brefs délais.</p>
                <button onClick={() => setShowSuccess(false)}>Fermer</button>
              </SuccessMessage>
            </SuccessOverlay>
          )}
        </AnimatePresence>
      </Container>
    </ContactSection>
  );
};

export default Contact;
